/**
 * User Type Definitions (JS version)
 * Defines the structure for user invitations and management
 */

// “Enum” de scopes (en JS lo representamos como constantes)
export const ACCESS_SCOPES = {
  ORGANIZATION: 'organization',
  FACILITY: 'facility',
  MULTI_FACILITY: 'multi-facility',
};

// Labels (equivalente a tu Record<AccessScope, ...>)
export const ACCESS_SCOPE_LABELS = {
  [ACCESS_SCOPES.ORGANIZATION]: {
    title: 'Organization Administrator',
    description: 'Full access across all facilities.',
    shortLabel: 'Organization-wide',
  },
  [ACCESS_SCOPES.FACILITY]: {
    title: 'Facility Administrator',
    description: 'Manages one specific facility.',
    shortLabel: 'Single Facility',
  },
  [ACCESS_SCOPES.MULTI_FACILITY]: {
    title: 'Multi-facility Administrator',
    description: 'Choose which facilities they can manage.',
    shortLabel: 'Multiple Facilities',
  },
};

/**
 * Role display names for accessScope (table/list views).
 * Uses ACCESS_SCOPE_LABELS.title for consistency with the invite/edit modal; fallback for legacy scopes.
 */
export const ACCESS_SCOPE_ROLE_DISPLAY = {
  [ACCESS_SCOPES.ORGANIZATION]: ACCESS_SCOPE_LABELS[ACCESS_SCOPES.ORGANIZATION].title,
  [ACCESS_SCOPES.FACILITY]: ACCESS_SCOPE_LABELS[ACCESS_SCOPES.FACILITY].title,
  [ACCESS_SCOPES.MULTI_FACILITY]: ACCESS_SCOPE_LABELS[ACCESS_SCOPES.MULTI_FACILITY].title,
  'primary-organization': 'Primary Admin',
};

/**
 * Returns the role label for a given accessScope (for table/list). Prefer this over raw ACCESS_SCOPE_ROLE_DISPLAY.
 */
export function getRoleDisplayName(accessScope) {
  return ACCESS_SCOPE_ROLE_DISPLAY[accessScope] ?? ACCESS_SCOPE_LABELS[accessScope]?.title ?? 'Editor';
}

/** Legacy role display (admin/manager/staff) for modals */
export const LEGACY_ROLE_DISPLAY = {
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
};

/** Badge CSS class by scope/role (for modals) */
export const SCOPE_BADGE_CLASSES = {
  'primary-organization': 'bg-purple-100 text-purple-700',
  primary_admin: 'bg-purple-100 text-purple-700',
  organization: 'bg-blue-100 text-blue-700',
  organization_admin: 'bg-blue-100 text-blue-700',
  'multi-facility': 'bg-green-100 text-green-700',
  facility: 'bg-orange-100 text-orange-700',
  admin: 'bg-purple-100 text-purple-700',
  manager: 'bg-blue-100 text-blue-700',
  default: 'bg-gray-100 text-gray-700',
};

/**
 * (Opcional) Helpers / “shape” docs
 * En JS no hay interfaces, pero podés documentar el shape con JSDoc.
 */

/**
 * @typedef {'organization' | 'facility' | 'multi-facility'} AccessScope
 */

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} organizationId
 * @property {string} fullName
 * @property {string} email
 * @property {string} phoneNumber
 * @property {AccessScope} accessScope
 * @property {string[]=} facilityIds
 * @property {Date} createdAt
 * @property {Date} updatedAt
 */

/**
 * @typedef {Object} CreateUserInput
 * @property {string} organizationId
 * @property {string} fullName
 * @property {string} email
 * @property {string} phoneNumber
 * @property {AccessScope} accessScope
 * @property {string[]=} facilityIds
 */

/**
 * @typedef {Object} UpdateUserInput
 * @property {string=} fullName
 * @property {string=} email
 * @property {string=} phoneNumber
 * @property {AccessScope=} accessScope
 * @property {string[]=} facilityIds
 */
